import { Box, Typography } from '@mui/material';
import { useListContext, usePermissions, useTranslate } from 'react-admin';
import { formatTimeInterval } from '@/helpers/date/formatTimeInterval.js';
import { formatNumberWithComma } from '@/helpers/format/formatNumber.js';
import { Growth, StyledChip } from '@/components/analytics/common/Growth.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { styled } from '@mui/material/styles';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';

const StyledServiceChangeAnalytics = styled(Box)(() => ({
    '& .ranking-header': {
        ...analyticsStyles.rankingHeader,
    },
    '& .progress-bars-header': {
        borderTop: `1px solid ${Palette.gray.gray100}`,
        borderBottom: `1px solid ${Palette.gray.gray100}`,
        padding: '24px 0',
        marginTop: '29px',
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            padding: '16px 0',
            marginTop: '8px',
        },
        '& .MuiTypography-root': {
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: '20px',
            letterSpacing: '-0.14px',
            marginBottom: '4px',
            '&.MuiTypography-h3': {
                fontSize: '34px',
                fontWeight: '700',
                lineHeight: '36px',
                letterSpacing: '-1.02px',
                marginBottom: '0',
                [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                    fontSize: '24px',
                    lineHeight: '28px',
                },
                '& .MuiChip-root': {
                    marginTop: '-4px',
                },
            },
        },
    },
}));

const StyledElement = styled(Box)(() => ({
    ...analyticsStyles.element,
    '&.element + .element': {
        marginTop: '25px',
    },
}));

const StyledProgressBarContainer = styled(Box)(() => ({
    marginTop: '8px',
    '& .progress-container': {
        position: 'relative',
        borderRadius: '2px',
        height: '6px',
        width: '100%',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            height: '2px',
            backgroundColor: Palette.gray.gray100,
            transform: 'translateY(-50%)',
        },
    },
    '& .progress-bar': {
        position: 'relative',
        zIndex: 1,
        height: '100%',
        borderRadius: '2px',
        transition: 'width 0.3s ease-in-out',
    },
}));

const ElementGrowth = ({ value, color }) => {
    if (!isFinite(value)) {
        return null;
    }

    return <StyledChip label={`${value > 0 ? '+' : ''}${formatNumberWithComma(value, 0)}%`} sx={{ backgroundColor: color }} />;
};

const Title = ({ currentAvgTime, previousAvgTime }) => {
    const translate = useTranslate();
    const formattedCurrentAvgTime = isFinite(currentAvgTime) ? formatTimeInterval(currentAvgTime) : 0;

    return (
        <StyledServiceChangeAnalytics>
            <Box className='ranking-header'>
                <Typography variant='h6'>{translate('analytics.service.avgDuration')}</Typography>
            </Box>
            {formattedCurrentAvgTime === 0 ? (
                <></>
            ) : (
                <Box className='progress-bars-header'>
                    <Typography variant='body1'>{translate('analytics.service.avgDurationLabel')}</Typography>
                    <Typography variant='h3'>
                        {formattedCurrentAvgTime}
                        <Growth value={(currentAvgTime - previousAvgTime) / previousAvgTime} reverse={true} />
                    </Typography>
                </Box>
            )}
        </StyledServiceChangeAnalytics>
    );
};

export const ProgressBar = ({ percentage, backgroundColor }) => (
    <StyledProgressBarContainer>
        <Box className='progress-container'>
            <Box
                className='progress-bar'
                sx={{
                    width: `${percentage}%`,
                    backgroundColor: backgroundColor,
                }}
            />
        </Box>
    </StyledProgressBarContainer>
);

const Element = ({ item, total, isMax }) => {
    const formattedAvgTime = isFinite(item.avgTime) ? formatTimeInterval(item.avgTime) : 0;
    const percentage = isFinite(item.avgTime) && total > 0 ? (item.avgTime * 100) / total : 0;
    const growthValue = (item.avgTime - item.previousAvgTime) / item.previousAvgTime;

    const orangeColor = Palette.yellow.yellow600;
    const yellowColor = Palette.yellow.yellow200;
    const greenColor = Palette.green.green500;
    const redColor = Palette.red.red200;
    const grayColor = Palette.gray.gray300;

    const growthColor = isMax ? yellowColor : growthValue === 0 || isNaN(growthValue) ? grayColor : growthValue < 0 ? greenColor : redColor;
    const progressBarColor = isMax ? orangeColor : greenColor;

    return (
        <StyledElement className='element element--service-requests'>
            <Box className='element-header'>
                <Box className='element-left'>
                    <Typography className='progress-bar-label' variant='caption'>
                        {item.name}
                    </Typography>
                </Box>
                <Box className='element-right'>
                    <Typography className='time-value'>{formattedAvgTime}</Typography>
                    <ElementGrowth value={growthValue} color={growthColor} />
                </Box>
                {isFinite(percentage) ? <Typography className='percentage'>{formatNumberWithComma(percentage, 1)}%</Typography> : null}
            </Box>
            <ProgressBar percentage={percentage} backgroundColor={progressBarColor} />
        </StyledElement>
    );
};

const serviceRequestsStatuses = [
    { id: 'new', nameKey: 'analytics.service.status.new', duration: 'serviceRequestNewDurationSum', count: 'serviceRequestNewCount' },
    { id: 'accepted', nameKey: 'analytics.service.status.accepted', duration: 'serviceRequestAcceptedDurationSum', count: 'serviceRequestAcceptedCount' },
    {
        id: 'failedContact',
        nameKey: 'analytics.service.status.failedContact',
        duration: 'serviceRequestFailedContactDurationSum',
        count: 'serviceRequestFailedContactCount',
    },
    { id: 'pending', nameKey: 'analytics.service.status.pending', duration: 'serviceRequestPendingDurationSum', count: 'serviceRequestPendingCount' },
    {
        id: 'inProgress',
        nameKey: 'analytics.service.status.inProgress',
        duration: 'serviceRequestInProgressDurationSum',
        count: 'serviceRequestInProgressCount',
    },
    { id: 'fixed', nameKey: 'analytics.service.status.fixed', duration: 'serviceRequestFixedDurationSum', count: 'serviceRequestFixedCount' },
];

export const ServiceChangeAnalytics = () => {
    const translate = useTranslate();
    const { data } = useListContext();
    const permissions = usePermissions();
    const hasAccess = !permissions?.permissions?.isService;

    if (!data || data.length === 0) return null;

    const analytic = data.find(s => s.id === 'current').data;
    const previous = data.find(s => s.id === 'previous').data;

    const currentAvgTime = analytic.serviceRequestDurationSum / analytic.serviceRequestCount;
    const previousAvgTime = previous.serviceRequestDurationSum / previous.serviceRequestCount;

    const items = serviceRequestsStatuses.map(item => {
        const avgTime = analytic[item.duration] / analytic[item.count];
        return {
            id: item.id,
            name: translate(item.nameKey),
            avgTime,
            previousAvgTime: previous[item.duration] / previous[item.count],
        };
    });

    const validItems = items.filter(item => isFinite(item.avgTime));
    const maxAvgTime = validItems.length > 0 ? Math.max(...validItems.map(item => item.avgTime)) : null;

    return (
        <Box
            sx={{
                backgroundColor: Palette.white,
                padding: '40px 40px 48px',
                borderRadius: '16px',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    padding: '24px',
                },
                marginTop: hasAccess ? '26px' : '0',
            }}
        >
            <Title currentAvgTime={currentAvgTime} previousAvgTime={previousAvgTime} />

            <NotificationBox variant='info' className='large'>
                {translate('analytics.service.avgDurationAdditionalInfo')}
            </NotificationBox>
            <Box sx={{ marginTop: '26px' }}>
                {analytic.serviceRequestDurationSum > 0 ? (
                    items.map(item => <Element key={item.id} item={item} total={analytic.serviceRequestDurationSum} isMax={item.avgTime === maxAvgTime} />)
                ) : (
                    <NoDataPlaceholder />
                )}
            </Box>
        </Box>
    );
};
